import React from 'react';
import styles from './table.module.css';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { SortOrder, TableColumn, User } from '../../types';
import { Loading } from '../loading';
import { SortOrderEnum } from '../../constants';
import { useTranslation } from 'react-i18next';

type TableProps<User> = {
  data?: User[];
  columns: TableColumn[];
  onSort?: (column: string) => void;
  sortConfig?: {
    column: string;
    direction: SortOrder;
  } | null;
  onEdit: (row: User) => void;
};

export const Table = ({ data, columns, onSort, sortConfig, onEdit }: TableProps<User>) => {
  const { t } = useTranslation();
  const { loading } = useSelector((state: RootState) => state.organization);
  const handleSort = (column: string) => {
    if (onSort) {
      onSort(column);
    }
  };

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          {columns.map((column) => (
            <th
              key={column.header}
              onClick={() => column.sortable && handleSort(column.accessor as string)}
              className={column.sortable ? styles.sortable : ''}
            >
              {column.header}
              {column.sortable && sortConfig?.column === column.accessor && (
                <span className={styles.sortIndicator}>
                  {sortConfig?.direction === SortOrderEnum.ASC ? '↑' : '↓'}
                </span>
              )}
            </th>
          ))}
          <th>{t('edit')}</th>
        </tr>
      </thead>
      <tbody>
        {!loading && data?.length ? (
          data.map((row) => (
            <tr key={`${row.id}-${row.organizationId}`}>
              {columns.map((column) => (
                <td key={`${row.id}-${column.header as string}`}>
                  {column.render ? column.render(row) : (row[column.accessor] as React.ReactNode)}
                </td>
              ))}
              <td key={`${row.id}-${row.planId}`}>
                {!row.deletedAt && row.isEnabled ? (
                  <i
                    onClick={() => onEdit(row)}
                    className={styles.editButton}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </i>
                ) : null}
              </td>
            </tr>
          ))
        ) : loading ? (
          <tr>
            <td
              colSpan={columns.length + 1}
              className={styles.loadingRow}
            >
              <Loading />
            </td>
          </tr>
        ) : (
          <tr>
            <td
              colSpan={columns.length + 1}
              className={`${styles.loadingRow} ${styles.noData}`}
            >
              {t('noData')}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};
